import {
  type PaperProps,
  Stack,
  type SxProps,
  type Theme,
  Typography,
} from "@mui/material";
import { forwardRef, type ReactNode } from "react";

import { AnimatedGradientBorder } from "../animated-gradient-border/AnimatedGradientBorder";
import { EmptySvg, ErrorNoInfoSvg } from "../assets/svg";
import type { DialogProps } from "../dialog/Dialog";
import { Dialog } from "../dialog/Dialog";
import { MaraLoader } from "../mara-loader/MaraLoader";
import { Markdown } from "../markdown/Markdown";
import { StatusMessage } from "../status-message/StatusMessage";
import { Tag, type TagProps } from "../tag/tag";
import { HEADING_PREFIX, splitFirstHeading } from "./utils";

const LOADING_TEXT = "Mara está analizando la tabla...";

const HIGHLIGHT_SX: SxProps<Theme> = (theme) => ({
  width: "fit-content",
  backgroundColor: theme.palette.accent.L90,
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.spacing(0.5),
  fontWeight: theme.typography.fontWeightSemiBold,
  color: theme.palette.secondary.dark,
});

const EMPTY_HEADING = "Análisis Pendiente";

// Adapts Dialog's Paper slot (elevation/square/variant) to AnimatedGradientBorder's Stack props
const GradientPaper = forwardRef<HTMLDivElement, PaperProps>(
  function GradientPaper({ elevation, square, variant, sx, ...props }, ref) {
    return (
      <AnimatedGradientBorder
        ref={ref}
        // Swapping out MUI's Paper drops its base positioning/scroll styles, so restore them here
        sx={[
          {
            position: "relative",
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            maxHeight: "calc(100% - 64px)",
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...props}
      />
    );
  },
);

export interface InsightDialogProps
  extends Omit<DialogProps, "content" | "title" | "actions" | "children"> {
  /**
   * Markdown-formatted string rendered as the dialog content.
   */
  content?: string;

  /**
   * List of tags rendered at the bottom of the dialog, forwarded as `Tag` props (label, icons, variant).
   * @default [{ label: "AskMara" }]
   */
  tags?: TagProps[];

  /**
   * When true, displays a fixed empty state instead of markdown content.
   * `error` takes precedence over it.
   * @default false
   */
  isEmpty?: boolean;

  /**
   * When true, displays a loading state while the insight is being generated.
   * Takes precedence over `isEmpty` and `content`.
   * @default false
   */
  isLoading?: boolean;
  /**
   * When true, displays a fixed error state instead of markdown content.
   * @default false
   */
  error?: boolean;
}

export function InsightDialog({
  content,
  tags = [{ label: "AskMara Insights" }],
  isEmpty = false,
  isLoading = false,
  open,
  error,
  onClose,
  ...rest
}: InsightDialogProps) {
  let bodyNode: ReactNode = null;

  let srOnlyTitle = EMPTY_HEADING;

  if (isLoading) {
    srOnlyTitle = LOADING_TEXT;
    bodyNode = (
      <Stack gap={1} alignItems="center" my={4}>
        <MaraLoader width="64px" />
        <Typography variant="body2" color="text.secondary">
          {LOADING_TEXT}
        </Typography>
      </Stack>
    );
  } else if (error) {
    bodyNode = (
      <Stack gap={2}>
        <Markdown componentSx={{ h2: HIGHLIGHT_SX }}>
          ## Ocurrió un error
        </Markdown>
        <Stack gap={2} py={4}>
          <StatusMessage
            title="¡Oh no! Algo salió mal"
            description="Por favor, intenta recargando la página."
            icon={<ErrorNoInfoSvg />}
          />
        </Stack>
      </Stack>
    );
  } else if (isEmpty) {
    bodyNode = (
      <Stack gap={2}>
        <Markdown componentSx={{ h1: HIGHLIGHT_SX }}>
          {`# ${EMPTY_HEADING}`}
        </Markdown>
        <Stack gap={2} py={4}>
          <StatusMessage
            title="Nada por aquí"
            description="No hay datos suficientes para este insight"
            icon={<EmptySvg width={64} height={64} aria-hidden="true" />}
          />
        </Stack>
      </Stack>
    );
  } else {
    const { heading, rest: remainingContent } = splitFirstHeading(
      content ?? "",
    );

    if (heading) {
      srOnlyTitle = heading.replace(HEADING_PREFIX, "");
    }

    bodyNode = (
      <Stack gap={2}>
        {heading && (
          <Markdown componentSx={{ h1: HIGHLIGHT_SX, h2: HIGHLIGHT_SX }}>
            {heading}
          </Markdown>
        )}
        {remainingContent && <Markdown>{remainingContent}</Markdown>}
      </Stack>
    );
  }

  const tagsNode = (
    <Stack direction="row" gap={1} flexWrap="wrap">
      {tags.map((tag, index) => (
        <Tag key={`${tag.label}-${index}`} {...tag} />
      ))}
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      actions={undefined}
      srOnlyTitle={srOnlyTitle}
      slots={{ paper: GradientPaper }}
      content={
        <Stack gap={2} m={2}>
          {bodyNode}
          {tagsNode}
        </Stack>
      }
      sx={{
        ".MuiDialog-paper": {
          width: "480px",
        },
      }}
      {...rest}
    />
  );
}
